import request from '@/utils/request'
import type { AxiosResponse } from 'axios'
import type { LaravelResource } from '../types'
import type { RunningTimer, TaskTimeEntry, TimeEntriesMeta } from '@/models/TaskTimeEntry'

/** Booking list plus the summary the effort bar needs. */
export interface TimeEntriesListResponse {
  data: TaskTimeEntry[]
  meta: TimeEntriesMeta
}

export interface TimeEntryPayload {
  /** ms timestamp */
  started_at?: number
  duration_minutes?: number
  description?: string | null
  /** Billing flag only; integer 0-100. */
  discount_percent?: number
  billing_contact_id?: string | null
}

/** GET /api/v2/tasks/{id}/time-entries — unpaginated, includes meta */
export function listTimeEntries(
  task_id: string,
): Promise<AxiosResponse<TimeEntriesListResponse>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/time-entries',
    method: 'get',
  })
}

/** POST /api/v2/tasks/{id}/time-entries */
export function createTimeEntry(
  task_id: string,
  payload: TimeEntryPayload,
): Promise<AxiosResponse<LaravelResource<TaskTimeEntry>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/time-entries',
    method: 'post',
    data: payload,
  })
}

/** PATCH /api/v2/tasks/{task_id}/time-entries/{entry_id} */
export function updateTimeEntry(
  task_id: string,
  entry_id: string,
  payload: TimeEntryPayload,
): Promise<AxiosResponse<LaravelResource<TaskTimeEntry>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/time-entries/' + entry_id,
    method: 'patch',
    data: payload,
  })
}

/** DELETE /api/v2/tasks/{task_id}/time-entries/{entry_id} */
export function deleteTimeEntry(
  task_id: string,
  entry_id: string,
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/time-entries/' + entry_id,
    method: 'delete',
  })
}

/**
 * POST /api/v2/tasks/{id}/timer/start
 *
 * Answers 422 with `{ running_timer }` when another timer is already running — the caller is
 * expected to ask the user rather than to overwrite it.
 */
export function startTimer(
  task_id: string,
  description?: string | null,
): Promise<AxiosResponse<LaravelResource<RunningTimer>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/timer/start',
    method: 'post',
    data: { description: description ?? null },
  })
}

/** POST /api/v2/tasks/{id}/timer/stop — books the elapsed time */
export function stopTimer(
  task_id: string,
  description?: string | null,
  billing?: { billing_contact_id?: string | null; discount_percent?: number },
): Promise<AxiosResponse<LaravelResource<TaskTimeEntry>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/timer/stop',
    method: 'post',
    data: { description: description ?? null, ...billing },
  })
}

/** DELETE /api/v2/tasks/{id}/timer — drops the timer without booking anything */
export function discardTimer(task_id: string): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/timer',
    method: 'delete',
  })
}

/** GET /api/v2/profile/timer — the current user's running timer, `data: null` when idle */
export function getRunningTimer(): Promise<AxiosResponse<LaravelResource<RunningTimer | null>>> {
  return request({
    url: '/api/v2/profile/timer',
    method: 'get',
  })
}

/** GET /api/v2/tasks/time-entries/export — 202, the report arrives by mail */
export function exportTimeEntries(): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v2/tasks/time-entries/export',
    method: 'get',
  })
}
